import React, { useMemo } from "react";
import bdePackages from "../../../JsonFiles/RegisterNewCoursePackages.json";
import inVehicleOptions from "../../../JsonFiles/NewCourseInvehicleTrainingOptions.json";
import driveTestOptions from "../../../JsonFiles/NewCourseDriveTestOptions.json";
import "../../CourseEnrollment/Payment/Components/paymentDetails/paymentDetails.css";

const CUSTOM_HOUR_RATE = 50;

const BDE_LABEL = "BDE MTO approved Certificate Training Course";
const IN_VEHICLE_LABEL = "In-Vehicle Training / Practice";
const DRIVE_TEST_LABEL = "Book a Vehicle for Drive Test (within city area)";

export function buildPaymentSummaryLines(selectionState) {
  const { sections } = selectionState || {};
  const { bde, inVehicle, driveTest } = sections || {};
  const lines = [];

  if (bde?.checked && bde?.packageId != null) {
    const pkg = bdePackages.find((p) => p.id === bde.packageId);
    if (pkg) {
      lines.push({
        key: "bde",
        label: BDE_LABEL,
        detail: pkg.name,
        price: Number(pkg.price) || 0,
      });
    }
  }

  if (inVehicle?.checked && inVehicle?.optionIndex != null) {
    const option = inVehicleOptions[inVehicle.optionIndex];
    if (option) {
      if (option.custom === true) {
        const hours = Number(inVehicle.customHours) || 0;
        const price = hours * CUSTOM_HOUR_RATE;
        lines.push({
          key: "inVehicle",
          label: IN_VEHICLE_LABEL,
          detail:
            hours > 0
              ? `Custom ${hours} hr${hours === 1 ? "" : "s"} ($${CUSTOM_HOUR_RATE}/hr)`
              : "Custom Hours",
          price,
        });
      } else {
        lines.push({
          key: "inVehicle",
          label: IN_VEHICLE_LABEL,
          detail: `${option.hours} hrs in-vehicle training`,
          price: Number(option.price) || 0,
        });
      }
    }
  }

  if (driveTest?.checked && driveTest?.optionIndex != null) {
    const option = driveTestOptions[driveTest.optionIndex];
    if (option) {
      lines.push({
        key: "driveTest",
        label: DRIVE_TEST_LABEL,
        detail: option.label,
        price: Number(option.price) || 0,
      });
    }
  }

  const total = lines.reduce((sum, line) => sum + (line.price || 0), 0);
  return { lines, total };
}

function formatMoney(amount) {
  return `$ ${Number(amount || 0).toFixed(2)}`;
}

const PaymentSummary = ({ selectionState }) => {
  const { lines, total } = useMemo(
    () => buildPaymentSummaryLines(selectionState),
    [selectionState]
  );

  const selectedPackageName = useMemo(() => {
    const packageLine = lines.find((line) => line.key === "bde");
    if (packageLine?.detail) return packageLine.detail;
    if (lines.length === 1) return lines[0].detail || lines[0].label;
    if (lines.length > 1) return "Multiple Services";
    return "None";
  }, [lines]);

  if (selectionState?.licenseType === "G1") {
    return null;
  }

  if (!lines.length) {
    return null;
  }

  return (
    <div className="w-full flex justify-center px-4 py-20">
      <div className="paymentBoxShadow relative w-full max-w-[500px] rounded-[19px] border bg-white">
        <div className="font-segoe-ui gap-4">
          <div
            className="flex w-full flex-col justify-center py-[20px] rounded-t-[19px]"
            style={{
              background:
                "transparent linear-gradient(90deg, #5DDDE7 0%, #8B56FF 100%) 0% 0% no-repeat padding-box",
            }}
          >
            <div className="text-center">
              <img
                className="mx-auto w-[101px]"
                src="/assets/OtherSectionImages/paymentDetailsImage_2.svg"
                alt="Payment details summary for selected BDE course package"
              />
            </div>
            <h2 className="text-center text-4xs text-[#000000] md:text-xs">
              Payment Details
            </h2>
            <p className="text-center text-[#404040]">
              Package Selected :{" "}
              <span className="text-5xs text-black md:text-[19px]">
                {selectedPackageName}
              </span>
            </p>
          </div>

          <div className="flex flex-col gap-4 py-[5px] px-4 mt-[10px]">
            <h2 className="text-[16px] font-bold">Payment Details</h2>

            {lines.map((line) => (
              <div key={line.key} className="flex">
                <p className="w-[75%] pr-2">{line.label}</p>
                <p className="w-[25%] text-right">{formatMoney(line.price)}</p>
              </div>
            ))}
          </div>

          <hr className="color-[#707070] my-3 mx-3" />

          <div className="flex font-semibold px-4 pb-4">
            <p className="w-[75%]">Total Payable</p>
            <p className="w-[25%] text-right">{formatMoney(total)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSummary;
